import { inject } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AuthService } from '@/app/auth/services/auth.service';

export const httpErrorHandlingInterceptor: HttpInterceptorFn = (
    request: HttpRequest<any>,
    next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
    const router = inject(Router);
    const messageService = inject(MessageService);
    const authService = inject(AuthService);

    return next(request).pipe(
        mergeMap((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
                if (
                    event.body &&
                    typeof event.body === 'object' &&
                    'statusCode' in event.body &&
                    event.body.statusCode !== 200
                ) {
                    const customError = new HttpErrorResponse({
                        error: event.body,
                        status: event.body.statusCode,
                        statusText: 'Error',
                        url: event.url || request.url,
                    });
                    throw customError;
                }
                if (event.body instanceof Blob && event.body.type === 'application/json') {
                    return from(event.body.text()).pipe(
                        mergeMap((text) => {
                            try {
                                const parsed = JSON.parse((text ?? '').trim());
                                if (parsed?.isSuccess === false) {
                                    throw new HttpErrorResponse({
                                        error: parsed,
                                        status: 400,
                                        statusText: 'Error',
                                        url: request.url,
                                    });
                                }
                            } catch (error) {
                                if (error instanceof HttpErrorResponse) throw error;
                            }
                            return of(event);
                        })
                    );
                }
            }
            return of(event);
        }),
        catchError((error: any) => {
            if (error instanceof HttpErrorResponse) {
                if (error.error instanceof Blob && error.error.type === 'application/json') {
                    return from(error.error.text()).pipe(
                        mergeMap((text) => {
                            const trimmed = (text ?? '').trim();
                            let parsedError: any = {};
                            
                            try {
                                parsedError = trimmed ? JSON.parse(trimmed) : {};
                            } catch {
                                parsedError = { rawBody: trimmed };
                            }
                            
                            return handleParsedError(error.status, parsedError, router, messageService, authService);
                        })
                    );
                } else {
                    return handleParsedError(error.status, error.error, router, messageService, authService);
                }
            } else {
                return throwError(() => error);
            }
        })
    );
};

const handleParsedError = (
    status: number,
    errorObj: any,
    router: Router,
    messageService: MessageService,
    authService: AuthService
): Observable<never> => {
    switch (status) {
        case 401:
            return handle401Error(router, messageService, authService);
        case 403:
            return handle403Error(messageService);
        default:
            messageService.add({
                severity: 'error',
                summary: 'Error',
                detail:
                    errorObj?.errors?.map((err: any) => err.message)?.join('\n') ||
                    'Something went wrong! Please try again.',
            });
            return throwError(() => errorObj);
    }
};

const handle401Error = (router: Router, messageService: MessageService, authService: AuthService) => {
    messageService.add({
        severity: 'warn',
        summary: 'Session Expired',
        detail: 'Your session has expired. Please login again.',
    });
    authService.logout();
    return throwError(() => new Error('Unauthorized'));
};

const handle403Error = (messageService: MessageService) => {
    messageService.add({
        severity: 'warn',
        summary: 'Unauthorized',
        detail: 'You do not have permission to access this resource.',
    });
    return throwError(() => new Error('Forbidden request'));
};
